import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import html2pdf from 'html2pdf.js';

import * as api from '../../services/api';
import { buildResearchProjectDocxBlob, triggerDocxDownload } from './buildResearchProjectDocx';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type Config = Record<string, unknown>;

function deepClone(c: Config): Config {
  return JSON.parse(JSON.stringify(c ?? {})) as Config;
}

function asStr(v: unknown): string {
  return v == null ? '' : String(v);
}

function asStrArr(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x ?? ''));
}

/**
 * Strip ```json fences and leading chat junk; return the first balanced {...} object string, or null.
 * Handles strings so braces inside "scientific_references" do not end the object early.
 */
function extractFirstJsonObject(raw: string): string | null {
  let s = raw.trim();
  const jsonFence = s.match(/```json\s*\n?([\s\S]*?)\n?```/i);
  if (jsonFence) {
    s = jsonFence[1].trim();
  } else {
    const anyFence = s.match(/```\s*\n?([\s\S]*?)\n?```/);
    if (anyFence && /^\s*\{/.test(anyFence[1])) {
      s = anyFence[1].trim();
    }
  }
  const startMarkers = [/\{\s*"project_title"\s*:/, /\{\s*"title"\s*:/, /\{\s*"/];
  let start = -1;
  for (const re of startMarkers) {
    const m = re.exec(s);
    if (m && m.index !== undefined) {
      start = m.index;
      break;
    }
  }
  if (start < 0) start = s.indexOf('{');
  if (start < 0) return null;
  s = s.slice(start);
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
    } else {
      if (c === '"') inStr = true;
      else if (c === '{') depth++;
      else if (c === '}') {
        depth--;
        if (depth === 0) return s.slice(0, i + 1);
      }
    }
  }
  return null;
}

/* ——— Marketing-aligned document shell ——— */
const Root = styled.div`
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    'Helvetica Neue',
    Helvetica,
    Arial,
    sans-serif;
  padding: 0 24px 48px;
  max-width: 920px;
  margin: 0 auto;
  color: #0f172a;
`;

const StickyBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0 16px;
  margin-bottom: 8px;
  background: linear-gradient(to bottom, #f9fafb 92%, transparent);
  border-bottom: 1px solid #e2e8f0;
`;

const BarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Back = styled(Link)`
  font-size: 13px;
  color: #2563eb;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const BarTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  max-width: 28rem;
  line-height: 1.3;
`;

const TopActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
`;

const Btn = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost' | 'outline' }>`
  appearance: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 10px;
  ${(p) =>
    p.$variant === 'danger'
      ? `
    color: #fff;
    background: #b91c1c;
    &:hover:not(:disabled) { filter: brightness(1.05); }
  `
      : p.$variant === 'ghost'
        ? `
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    &:hover:not(:disabled) { background: #e5e7eb; }
  `
        : p.$variant === 'outline'
          ? `
    color: #1d4ed8;
    background: #fff;
    border: 1px solid #93c5fd;
    &:hover:not(:disabled) { background: #eff6ff; }
  `
          : `
    color: #fff;
    background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%);
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
    &:hover:not(:disabled) { filter: brightness(1.05); }
  `}
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
`;

const Err = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Ok = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  background: #ecfdf5;
  color: #047857;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Meta = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 24px;
`;

const Cover = styled.header`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  padding: 28px 32px 24px;
  margin-bottom: 20px;
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 42%, #0c4a6e 100%);
  color: #f8fafc;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.35);
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 20% 80%, rgba(56, 189, 248, 0.25) 0%, transparent 45%),
      radial-gradient(circle at 90% 10%, rgba(99, 102, 241, 0.35) 0%, transparent 40%);
    pointer-events: none;
  }
`;

const CoverInner = styled.div`
  position: relative;
  z-index: 1;
`;

const CoverTagline = styled.p`
  font-size: 13px;
  color: #7dd3fc;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const HeroTitle = styled.h1`
  font-size: clamp(22px, 3.5vw, 30px);
  font-weight: 800;
  margin: 0 0 12px 0;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const HeroLead = styled.p`
  font-size: 15px;
  color: #cbd5e1;
  line-height: 1.55;
  margin: 0 0 14px 0;
  max-width: 44rem;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #e2e8f0;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionKicker = styled.div`
  font-size: clamp(18px, 3vw, 26px);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: #0f172a;
  margin-bottom: 12px;
  padding-left: 12px;
  border-left: 5px solid #2563eb;
  border-radius: 0 8px 8px 0;
  padding-top: 4px;
  padding-bottom: 4px;
  background: linear-gradient(90deg, rgba(37, 99, 235, 0.06) 0%, transparent 55%);
`;

const H2 = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px 0;
`;

const Body = styled.p`
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #475569;
  line-height: 1.65;
`;

const Ul = styled.ul`
  margin: 0;
  padding-left: 1.15rem;
  color: #334155;
  font-size: 15px;
  line-height: 1.65;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 14px;
  margin-top: 12px;
`;

const Card = styled.div`
  padding: 16px 18px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
`;

const CardLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #0ea5e9;
  margin-bottom: 6px;
`;

const CardText = styled.div`
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
`;

const Inp = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 10px;
`;

const Txa = styled.textarea`
  width: 100%;
  min-height: 88px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
  margin-bottom: 12px;
  resize: vertical;
`;

const JsonArea = styled.textarea`
  width: 100%;
  min-height: 480px;
  padding: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  resize: vertical;
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-top: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 10px 12px;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: #f8fafc;
    font-weight: 700;
    color: #334155;
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

const PreBlock = styled.pre`
  font-size: 12px;
  line-height: 1.45;
  padding: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow-x: auto;
  color: #334155;
`;

const RefOrderedList = styled.ol`
  margin: 0;
  padding-left: 1.35rem;
  font-size: 14px;
  line-height: 1.55;
  color: #334155;
`;

const RefLi = styled.li`
  margin-bottom: 14px;
`;

/** Scoped rules for html2pdf capture (same pipeline as marketing portal). */
const ResearchPdfPrintStyles = createGlobalStyle`
  .research-pdf-body {
    box-sizing: border-box;
  }
  .research-pdf-body header {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .research-pdf-body table {
    font-size: 12px;
  }
  .research-pdf-body th,
  .research-pdf-body td {
    word-break: break-word;
  }
  .research-pdf-body tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .research-pdf-body thead {
    display: table-header-group;
  }
  .research-pdf-body pre {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const PdfCaptureWrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

/** Outside capture ref so the control is never in the PDF (mirrors marketing PostBrochureExport). */
const PostResearchExport = styled.div`
  max-width: 920px;
  margin: 24px auto 0;
  padding: 0 0 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ExportPdfBtn = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  color: #fff;
  background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%);
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

const ExportWordBtn = styled.button`
  appearance: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  color: #1d4ed8;
  background: #fff;
  border: 2px solid #2563eb;
  &:hover:not(:disabled) {
    background: #eff6ff;
  }
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

function pdfFilenameFromProject(title: string, shortFallback: string): string {
  const base = title.trim() || shortFallback.trim() || 'research-project';
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const name = (slug || 'research-project').slice(0, 100);
  return `${name}.pdf`;
}

function docxFilenameFromProject(title: string, shortFallback: string): string {
  return pdfFilenameFromProject(title, shortFallback).replace(/\.pdf$/i, '.docx');
}

type Mode = 'view' | 'edit-page' | 'edit-json';

function optionalBudgetList(budget: Record<string, unknown> | undefined): unknown[] {
  if (!budget) return [];
  const o = budget.optional_costs ?? budget.optional_categories;
  return Array.isArray(o) ? o : [];
}

/** Parse numeric budget fields (JSON may use strings). */
function numField(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Sample size for budget math: cohort.target_sample_size, else sum of arm.size, else null.
 */
function getEffectiveCohortN(cohort: Record<string, unknown>): number | null {
  const t = numField(cohort.target_sample_size);
  if (t != null && t > 0) return Math.round(t);
  const arms = cohort.arms;
  if (!Array.isArray(arms)) return null;
  let sum = 0;
  for (const a of arms) {
    if (a && typeof a === 'object' && !Array.isArray(a)) {
      const s = numField((a as Record<string, unknown>).size);
      if (s != null && s >= 0) sum += s;
    }
  }
  return sum > 0 ? Math.round(sum) : null;
}

function isPerPatientCostType(costType: unknown): boolean {
  const s = String(costType ?? '')
    .trim()
    .toLowerCase();
  return s === 'per_patient' || s === 'per patient' || s === 'per-participant' || s === 'per_participant';
}

type BudgetRowDisplay = {
  key: number;
  name: string;
  costType: string;
  unitCostDisp: string;
  unitsDisp: string;
  totalDisp: string;
  notes: string;
};

/** Category rows + subtotal; per-patient lines use cohort N when available. */
function computeBudgetCategoryRows(
  budget: Record<string, unknown>,
  cohort: Record<string, unknown>
): { cohortN: number | null; rows: BudgetRowDisplay[]; categorySubtotal: number } {
  const cohortN = getEffectiveCohortN(cohort);
  const categories = Array.isArray(budget.categories) ? budget.categories : [];
  let categorySubtotal = 0;
  const rows = categories.map((row, i) => {
    const r = row as Record<string, unknown>;
    const unitCost = numField(r.unit_cost);
    const jsonUnits = numField(r.units);
    const perPatient = isPerPatientCostType(r.cost_type);
    const unitsForMath =
      perPatient && unitCost != null ? cohortN ?? jsonUnits : jsonUnits;
    let lineTotal: number | null = null;
    if (perPatient && unitCost != null && unitsForMath != null && unitsForMath >= 0) {
      lineTotal = Math.round(unitCost * unitsForMath);
    } else {
      lineTotal = numField(r.total);
    }
    if (lineTotal != null && Number.isFinite(lineTotal)) {
      categorySubtotal += lineTotal;
    }
    const unitsStored = jsonUnits;
    const unitsMismatch =
      perPatient &&
      cohortN != null &&
      unitsStored != null &&
      Math.round(unitsStored) !== cohortN;
    const notesExtra = unitsMismatch
      ? ` (JSON had ${unitsStored} units; totals use cohort N = ${cohortN})`
      : '';
    return {
      key: i,
      name: asStr(r.name),
      costType: asStr(r.cost_type),
      unitCostDisp: unitCost != null ? String(unitCost) : '—',
      unitsDisp:
        perPatient && unitsForMath != null
          ? String(unitsForMath)
          : unitsStored != null
            ? String(unitsStored)
            : '—',
      totalDisp: lineTotal != null ? String(lineTotal) : '—',
      notes: asStr(r.notes) + notesExtra,
    };
  });
  return { cohortN, rows, categorySubtotal };
}

function computeOptionalSubtotal(budget: Record<string, unknown>): number {
  let s = 0;
  for (const row of optionalBudgetList(budget)) {
    const r = row as Record<string, unknown>;
    const t = numField(r.estimated_total ?? r.suggested_total);
    if (t != null && Number.isFinite(t)) s += t;
  }
  return s;
}

/** Categories (cohort-aware) + optional lines. */
function computeBudgetGrandTotalFromJson(
  budget: Record<string, unknown>,
  cohort: Record<string, unknown>
): number {
  const { categorySubtotal } = computeBudgetCategoryRows(budget, cohort);
  return Math.round(categorySubtotal + computeOptionalSubtotal(budget));
}

/** Returns config copy with budget.estimated_total set from line items. */
function withAutoBudgetEstimatedTotal(cfg: Config): Config {
  const c = deepClone(cfg);
  const b = c.budget;
  if (!b || typeof b !== 'object' || Array.isArray(b)) return c;
  const budget = b as Record<string, unknown>;
  const cohort =
    c.cohort && typeof c.cohort === 'object' && !Array.isArray(c.cohort)
      ? (c.cohort as Record<string, unknown>)
      : {};
  const grand = computeBudgetGrandTotalFromJson(budget, cohort);
  c.budget = { ...budget, estimated_total: grand };
  return c;
}

type ParsedBibRef = {
  entryType: string;
  citeKey: string;
  title?: string;
  author?: string;
  journal?: string;
  year?: string;
  raw: string;
};

function bibBraceField(block: string, name: string): string | undefined {
  const re = new RegExp(`${name}\\s*=\\s*\\{([^}]*)\\}`, 'i');
  const m = block.match(re);
  if (!m) return undefined;
  return m[1].replace(/\s+/g, ' ').trim() || undefined;
}

function splitBibtexBlob(text: string): string[] {
  const t = text.trim();
  if (!t) return [];
  return t.split(/(?=@\w+\s*\{)/i).map((s) => s.trim()).filter(Boolean);
}

function parseBibtexEntryBlock(block: string): ParsedBibRef | null {
  const t = block.trim();
  if (!t) return null;
  const head = t.match(/^@(\w+)\s*\{\s*([^,\s]+)\s*,/i);
  if (!head) return null;
  return {
    entryType: head[1].toLowerCase(),
    citeKey: head[2],
    title: bibBraceField(t, 'title'),
    author: bibBraceField(t, 'author'),
    journal: bibBraceField(t, 'journal') || bibBraceField(t, 'booktitle'),
    year: bibBraceField(t, 'year'),
    raw: t,
  };
}

function parseScientificReferences(v: unknown): ParsedBibRef[] {
  if (v == null) return [];
  if (typeof v === 'string') {
    return splitBibtexBlob(v).map(parseBibtexEntryBlock).filter((x): x is ParsedBibRef => x != null);
  }
  if (Array.isArray(v)) {
    const out: ParsedBibRef[] = [];
    v.forEach((item, i) => {
      if (typeof item === 'string') {
        const p = parseBibtexEntryBlock(item);
        if (p) out.push(p);
        else if (item.trim())
          out.push({ entryType: 'raw', citeKey: `ref-${i + 1}`, raw: item.trim() });
      } else if (item && typeof item === 'object' && !Array.isArray(item)) {
        const o = item as Record<string, unknown>;
        out.push({
          entryType: asStr(o.entry_type || 'article').toLowerCase() || 'article',
          citeKey: asStr(o.cite_key || o.key || `ref-${i + 1}`),
          title: asStr(o.title) || undefined,
          author: asStr(o.author) || undefined,
          journal: asStr(o.journal || o.booktitle) || undefined,
          year: asStr(o.year) || undefined,
          raw: JSON.stringify(item),
        });
      }
    });
    return out;
  }
  return [];
}

/** String for page-edit textarea: BibTeX blob, or pretty JSON, or array → synthetic BibTeX blocks. */
function scientificReferencesToEditString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v
      .map((item, i) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const o = item as Record<string, unknown>;
          const k = asStr(o.cite_key || o.key || `ref${i + 1}`);
          const et = asStr(o.entry_type || 'article') || 'article';
          const fieldLines: string[] = [];
          if (o.title != null && String(o.title).length) fieldLines.push(`  title={${String(o.title)}}`);
          if (o.author != null && String(o.author).length) fieldLines.push(`  author={${String(o.author)}}`);
          if (o.journal != null && String(o.journal).length) fieldLines.push(`  journal={${String(o.journal)}}`);
          else if (o.booktitle != null && String(o.booktitle).length)
            fieldLines.push(`  booktitle={${String(o.booktitle)}}`);
          if (o.year != null && String(o.year).length) fieldLines.push(`  year={${String(o.year)}}`);
          const withCommas = fieldLines.map((line, j) => (j < fieldLines.length - 1 ? `${line},` : line));
          return [`@${et}{${k},`, ...withCommas, `}`].join('\n');
        }
        return JSON.stringify(item);
      })
      .join('\n\n');
  }
  return JSON.stringify(v, null, 2);
}

function ReferenceFormatted({ r }: { r: ParsedBibRef }) {
  const hasAny = r.author || r.year || r.title || r.journal;
  if (!hasAny) {
    return <PreBlock style={{ margin: 0, fontSize: 12 }}>{r.raw}</PreBlock>;
  }
  return (
    <>
      {r.author ? <>{r.author.replace(/\s+and\s+/gi, ', ')}</> : null}
      {r.year ? <> ({r.year})</> : null}
      {r.title ? <>. {r.title}</> : null}
      {r.journal ? <>. <em>{r.journal}</em></> : null}
      .
    </>
  );
}

const SynopsisNarrative = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: #334155;
  margin: 0;
  white-space: pre-wrap;
`;

function isAcuteMechanisticProfile(data: Config, studyType: string): boolean {
  const t = studyType.toLowerCase();
  if (t.includes('mechanistic')) return true;
  if (t.includes('acute') && (t.includes('pilot') || t.includes('session') || t.includes('stimulation'))) return true;
  if (data.study_profile === 'acute_mechanistic') return true;
  if (data.acute_mechanistic_pilot === true) return true;
  const co = data.cohort;
  if (co && typeof co === 'object' && !Array.isArray(co)) {
    const c = co as Record<string, unknown>;
    const d = asStr(c.design || c.study_design).toLowerCase();
    if (d.includes('acute') || d.includes('mechanistic') || d === 'single_arm_acute') return true;
  }
  return false;
}

function armRecordIsSham(a: Record<string, unknown>): boolean {
  const role = asStr(a.role).toLowerCase();
  if (role === 'sham' || role === 'placebo') return true;
  return /\bsham\b|\bplacebo\b/i.test(asStr(a.name));
}

function shamArmsHaveParticipants(arms: unknown): boolean {
  if (!Array.isArray(arms)) return false;
  for (const arm of arms) {
    if (!arm || typeof arm !== 'object' || Array.isArray(arm)) continue;
    const a = arm as Record<string, unknown>;
    if (!armRecordIsSham(a)) continue;
    const n = numField(a.size);
    if (n != null && n > 0) return true;
  }
  return false;
}

/** Cohort arms shown in read view: omit sham arms with no enrolled N. */
function cohortArmsForReadView(arms: unknown): unknown[] {
  if (!Array.isArray(arms)) return [];
  return arms.filter((arm) => {
    if (!arm || typeof arm !== 'object' || Array.isArray(arm)) return false;
    const a = arm as Record<string, unknown>;
    if (armRecordIsSham(a)) {
      const n = numField(a.size);
      if (n == null || n <= 0) return false;
    }
    return true;
  });
}

function showShamProtocolReadView(intervention: Record<string, unknown>, cohort: Record<string, unknown>): boolean {
  if (!asStr(intervention.sham_protocol).trim()) return false;
  return shamArmsHaveParticipants(cohort.arms);
}

function visitRecordLabel(v: Record<string, unknown>): string {
  return asStr(v.visit || v.name || v.title);
}

function shouldOmitRandomizationVisit(
  v: Record<string, unknown>,
  mechanistic: boolean,
  data: Config
): boolean {
  if (!mechanistic) return false;
  if (data.include_randomization_visit === true) return false;
  const label = visitRecordLabel(v).toLowerCase();
  if (!/randomi[sz]ation|randomi[sz]ed/.test(label)) return false;
  const procs = Array.isArray(v.procedures) ? v.procedures : [];
  return procs.length === 0;
}

function humanizeFieldKey(k: string): string {
  return k
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Renders active_protocol as prose, bullets, or labeled lines—not raw JSON when avoidable. */
function ActiveProtocolView({ value }: { value: unknown }) {
  if (value == null) return null;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;
    return <Body style={{ marginBottom: 0 }}>{s}</Body>;
  }
  if (Array.isArray(value)) {
    const lines = value.map((x) => String(x ?? '').trim()).filter(Boolean);
    if (lines.length === 0) return null;
    return (
      <Ul style={{ marginTop: 0, marginBottom: 0 }}>
        {lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </Ul>
    );
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const entries = Object.entries(o).filter(([, v]) => {
      if (v == null) return false;
      if (typeof v === 'string' && !v.trim()) return false;
      if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0) return false;
      return true;
    });
    if (entries.length === 0) return null;
    return (
      <Ul style={{ marginTop: 0, marginBottom: 0 }}>
        {entries.map(([k, v]) => (
          <li key={k}>
            <strong>{humanizeFieldKey(k)}:</strong>{' '}
            {typeof v === 'object' && v !== null && !Array.isArray(v) ? (
              <span style={{ display: 'block', marginTop: 6 }}>
                <ActiveProtocolView value={v} />
              </span>
            ) : Array.isArray(v) ? (
              <ActiveProtocolView value={v} />
            ) : (
              String(v)
            )}
          </li>
        ))}
      </Ul>
    );
  }
  return <PreBlock style={{ marginBottom: 0 }}>{JSON.stringify(value)}</PreBlock>;
}

function outcomeEndpointList(outcomes: Record<string, unknown>, key: 'primary' | 'secondary' | 'exploratory'): string[] {
  const plural =
    key === 'primary'
      ? 'primary_endpoints'
      : key === 'secondary'
        ? 'secondary_endpoints'
        : 'exploratory_endpoints';
  const arr = asStrArr(outcomes[plural]);
  if (arr.length > 0) return arr;
  const single = asStr(outcomes[key]).trim();
  return single ? [single] : [];
}

function activeProtocolIsNonempty(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return Boolean(v.trim());
  if (Array.isArray(v)) return v.some((x) => String(x ?? '').trim());
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return false;
}

function DocumentView({
  data,
  isPageEdit,
  onPatch,
}: {
  data: Config;
  isPageEdit: boolean;
  onPatch: (patch: Config) => void;
}) {
  const patchRoot = (key: string, val: unknown) => onPatch({ ...data, [key]: val });

  const projectTitle = asStr(data.project_title) || asStr(data.title);
  const shortTitle = asStr(data.short_title);
  const status = asStr(data.status);
  const studyType = asStr(data.study_type);
  const studySite = asStr(data.study_site);
  const areas = asStrArr(data.clinical_area);
  const summary = asStr(data.project_summary);
  const rationale = asStrArr(data.scientific_rationale);
  const objectives = (data.objectives && typeof data.objectives === 'object' && !Array.isArray(data.objectives)
    ? (data.objectives as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const hypothesis = asStr(data.hypothesis);
  const irb = (data.irb && typeof data.irb === 'object' && !Array.isArray(data.irb)
    ? (data.irb as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const cohort = (data.cohort && typeof data.cohort === 'object' && !Array.isArray(data.cohort)
    ? (data.cohort as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const eligibility = (data.eligibility && typeof data.eligibility === 'object' && !Array.isArray(data.eligibility)
    ? (data.eligibility as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const intervention = (data.intervention && typeof data.intervention === 'object' && !Array.isArray(data.intervention)
    ? (data.intervention as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const visits = Array.isArray(data.study_visits) ? data.study_visits : [];
  const outcomes = (data.outcomes && typeof data.outcomes === 'object' && !Array.isArray(data.outcomes)
    ? (data.outcomes as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const dataCollection = (data.data_collection && typeof data.data_collection === 'object' && !Array.isArray(data.data_collection)
    ? (data.data_collection as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const stats = (data.statistical_analysis && typeof data.statistical_analysis === 'object' && !Array.isArray(data.statistical_analysis)
    ? (data.statistical_analysis as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const budget = (data.budget && typeof data.budget === 'object' && !Array.isArray(data.budget)
    ? (data.budget as Record<string, unknown>)
    : {}) as Record<string, unknown>;

  const knownKeys = new Set([
    'project_title',
    'title',
    'short_title',
    'status',
    'study_type',
    'clinical_area',
    'study_site',
    'project_summary',
    'scientific_rationale',
    'objectives',
    'hypothesis',
    'irb',
    'cohort',
    'eligibility',
    'intervention',
    'study_visits',
    'outcomes',
    'data_collection',
    'statistical_analysis',
    'budget',
    'scientific_references',
    'protocol_synopsis_text',
  ]);
  const extraEntries = Object.entries(data).filter(([k]) => !knownKeys.has(k));

  const mechanistic = isAcuteMechanisticProfile(data, studyType);
  const visitsForView = useMemo(() => {
    return visits.filter((v) => {
      if (!v || typeof v !== 'object' || Array.isArray(v)) return true;
      return !shouldOmitRandomizationVisit(v as Record<string, unknown>, mechanistic, data);
    });
  }, [visits, mechanistic, data]);

  const parsedRefs = useMemo(() => parseScientificReferences(data.scientific_references), [data.scientific_references]);
  const referencesEditString = scientificReferencesToEditString(data.scientific_references);
  const protocolSynopsis = asStr(data.protocol_synopsis_text);

  return (
    <>
      <Cover>
        <CoverInner>
          <CoverTagline>Research project</CoverTagline>
          {isPageEdit ? (
            <>
              <Label>Project title</Label>
              <Txa
                value={asStr(data.project_title)}
                onChange={(e) => patchRoot('project_title', e.target.value)}
                style={{ minHeight: 72, color: '#0f172a', background: '#fff' }}
              />
              <Label>Short title</Label>
              <Inp value={shortTitle} onChange={(e) => patchRoot('short_title', e.target.value)} />
              <Label>Status</Label>
              <Inp value={status} onChange={(e) => patchRoot('status', e.target.value)} />
              <Label>Study type</Label>
              <Txa value={studyType} onChange={(e) => patchRoot('study_type', e.target.value)} style={{ minHeight: 56, color: '#0f172a', background: '#fff' }} />
              <Label>Study site</Label>
              <Inp value={studySite} onChange={(e) => patchRoot('study_site', e.target.value)} />
              <Label>Clinical areas (one per line)</Label>
              <Txa
                value={areas.join('\n')}
                onChange={(e) =>
                  patchRoot(
                    'clinical_area',
                    e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean)
                  )
                }
              />
            </>
          ) : (
            <>
              <HeroTitle>{projectTitle || 'Untitled research project'}</HeroTitle>
              {shortTitle && <HeroLead style={{ color: '#e2e8f0' }}>{shortTitle}</HeroLead>}
              <PillRow>
                {status && <Pill>{status}</Pill>}
                {studySite && <Pill>{studySite}</Pill>}
                {areas.map((a) => (
                  <Pill key={a}>{a}</Pill>
                ))}
              </PillRow>
              {studyType && (
                <HeroLead style={{ marginTop: 14 }}>{studyType}</HeroLead>
              )}
            </>
          )}
        </CoverInner>
      </Cover>

      {(isPageEdit || protocolSynopsis) && (
        <Section>
          <SectionKicker>Protocol synopsis</SectionKicker>
          {isPageEdit ? (
            <>
              <Body style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                Short collaboration-facing narrative (single-center design, arms, acute session framing, biomarkers). Shown
                below the title block in view and PDF.
              </Body>
              <Txa
                value={protocolSynopsis}
                onChange={(e) => patchRoot('protocol_synopsis_text', e.target.value)}
                style={{ minHeight: 140 }}
              />
            </>
          ) : (
            <SynopsisNarrative>{protocolSynopsis}</SynopsisNarrative>
          )}
        </Section>
      )}

      {(isPageEdit || summary) && (
        <Section>
          <SectionKicker>Summary</SectionKicker>
          {isPageEdit ? (
            <Txa value={summary} onChange={(e) => patchRoot('project_summary', e.target.value)} style={{ minHeight: 120 }} />
          ) : (
            summary && <Body>{summary}</Body>
          )}
        </Section>
      )}

      {(rationale.length > 0 || isPageEdit) && (
        <Section>
          <SectionKicker>Scientific rationale</SectionKicker>
          {isPageEdit ? (
            <Txa
              value={rationale.join('\n')}
              onChange={(e) =>
                patchRoot(
                  'scientific_rationale',
                  e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean)
                )
              }
            />
          ) : (
            <Ul>
              {rationale.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </Ul>
          )}
        </Section>
      )}

      {(isPageEdit ||
        asStr(objectives.primary).trim() ||
        asStr(objectives.secondary).trim() ||
        asStr(objectives.exploratory).trim()) && (
        <Section>
          <SectionKicker>Objectives</SectionKicker>
          <Grid2>
            {(['primary', 'secondary', 'exploratory'] as const).map((k) => {
              const text = asStr(objectives[k]);
              if (!isPageEdit && !text.trim()) return null;
              return (
                <Card key={k}>
                  <CardLabel style={{ textTransform: 'capitalize' }}>{k}</CardLabel>
                  {isPageEdit ? (
                    <Txa
                      value={text}
                      onChange={(e) =>
                        onPatch({
                          ...data,
                          objectives: { ...objectives, [k]: e.target.value },
                        })
                      }
                      style={{ minHeight: 100, marginBottom: 0 }}
                    />
                  ) : (
                    <CardText>{text}</CardText>
                  )}
                </Card>
              );
            })}
          </Grid2>
        </Section>
      )}

      {(isPageEdit || hypothesis) && (
        <Section>
          <SectionKicker>Hypothesis</SectionKicker>
          {isPageEdit ? (
            <Txa value={hypothesis} onChange={(e) => patchRoot('hypothesis', e.target.value)} />
          ) : (
            hypothesis && <Body>{hypothesis}</Body>
          )}
        </Section>
      )}

      <Section>
        <SectionKicker>IRB &amp; compliance</SectionKicker>
        {isPageEdit ? (
          <>
            {[
              ['review_category', 'Review category'],
              ['risk_level', 'Risk level'],
              ['exemption_reason', 'Exemption reason'],
            ].map(([key, lab]) => (
              <div key={key}>
                <Label>{lab}</Label>
                <Inp
                  value={asStr(irb[key])}
                  onChange={(e) => onPatch({ ...data, irb: { ...irb, [key]: e.target.value } })}
                />
              </div>
            ))}
            <Label>Safety monitoring (one per line)</Label>
            <Txa
              value={asStrArr(irb.safety_monitoring).join('\n')}
              onChange={(e) =>
                onPatch({
                  ...data,
                  irb: {
                    ...irb,
                    safety_monitoring: e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
            <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(irb.exemption_likely)}
                onChange={(e) => onPatch({ ...data, irb: { ...irb, exemption_likely: e.target.checked } })}
              />
              Exemption likely
            </Label>
            <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(irb.consent_required)}
                onChange={(e) => onPatch({ ...data, irb: { ...irb, consent_required: e.target.checked } })}
              />
              Consent required
            </Label>
            <Label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(irb.hipaa_authorization)}
                onChange={(e) => onPatch({ ...data, irb: { ...irb, hipaa_authorization: e.target.checked } })}
              />
              HIPAA authorization
            </Label>
          </>
        ) : (
          <Grid2>
            {Object.entries(irb).map(([k, v]) => {
              if (Array.isArray(v)) {
                return (
                  <Card key={k}>
                    <CardLabel>{k.replace(/_/g, ' ')}</CardLabel>
                    <Ul style={{ marginTop: 8 }}>
                      {v.map((x, i) => (
                        <li key={i}>{String(x)}</li>
                      ))}
                    </Ul>
                  </Card>
                );
              }
              if (typeof v === 'boolean') {
                return (
                  <Card key={k}>
                    <CardLabel>{k.replace(/_/g, ' ')}</CardLabel>
                    <CardText>{v ? 'Yes' : 'No'}</CardText>
                  </Card>
                );
              }
              if (v == null || v === '') return null;
              return (
                <Card key={k}>
                  <CardLabel>{k.replace(/_/g, ' ')}</CardLabel>
                  <CardText>{String(v)}</CardText>
                </Card>
              );
            })}
          </Grid2>
        )}
      </Section>

      <Section>
        <SectionKicker>Cohort</SectionKicker>
        {isPageEdit ? (
          <>
            <Label>Target sample size</Label>
            <Inp
              type="number"
              value={cohort.target_sample_size != null ? String(cohort.target_sample_size) : ''}
              onChange={(e) => {
                const n = e.target.value === '' ? null : Number(e.target.value);
                onPatch({ ...data, cohort: { ...cohort, target_sample_size: n } });
              }}
            />
            <Label>Population</Label>
            <Inp
              value={asStr(cohort.population)}
              onChange={(e) => onPatch({ ...data, cohort: { ...cohort, population: e.target.value } })}
            />
            <Label>Expected dropout rate</Label>
            <Inp
              value={asStr(cohort.expected_dropout_rate)}
              onChange={(e) => onPatch({ ...data, cohort: { ...cohort, expected_dropout_rate: e.target.value } })}
            />
            <Label>Follow-up duration (optional; leave blank for acute single-session designs)</Label>
            <Inp
              value={asStr(cohort.follow_up_duration)}
              onChange={(e) => onPatch({ ...data, cohort: { ...cohort, follow_up_duration: e.target.value } })}
            />
            <Label>Arms (JSON array: {`[{"name":"","size":0,"role":"active|sham"}]`})</Label>
            <Txa
              value={JSON.stringify(Array.isArray(cohort.arms) ? cohort.arms : [], null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (Array.isArray(parsed)) {
                    onPatch({ ...data, cohort: { ...cohort, arms: parsed } });
                  }
                } catch {
                  /* keep typing */
                }
              }}
              style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 12 }}
            />
          </>
        ) : (
          <Grid2>
            {cohort.target_sample_size != null && (
              <Card>
                <CardLabel>Target N</CardLabel>
                <CardText>{String(cohort.target_sample_size)}</CardText>
              </Card>
            )}
            {asStr(cohort.population) && (
              <Card>
                <CardLabel>Population</CardLabel>
                <CardText>{asStr(cohort.population)}</CardText>
              </Card>
            )}
            {cohortArmsForReadView(cohort.arms).map((arm, i) => {
              const a = arm as Record<string, unknown>;
              return (
                <Card key={i}>
                  <CardLabel>{asStr(a.name) || `Arm ${i + 1}`}</CardLabel>
                  <CardText>Planned size: {a.size != null ? String(a.size) : '—'}</CardText>
                </Card>
              );
            })}
            {asStr(cohort.expected_dropout_rate) && (
              <Card>
                <CardLabel>Expected dropout</CardLabel>
                <CardText>{asStr(cohort.expected_dropout_rate)}</CardText>
              </Card>
            )}
            {asStr(cohort.follow_up_duration) && (
              <Card>
                <CardLabel>Follow-up</CardLabel>
                <CardText>{asStr(cohort.follow_up_duration)}</CardText>
              </Card>
            )}
          </Grid2>
        )}
      </Section>

      {(isPageEdit ||
        asStrArr(eligibility.inclusion_criteria).length > 0 ||
        asStrArr(eligibility.exclusion_criteria).length > 0) && (
      <Section>
        <SectionKicker>Eligibility</SectionKicker>
        {isPageEdit ? (
          <>
            <Label>Inclusion (one per line)</Label>
            <Txa
              value={asStrArr(eligibility.inclusion_criteria).join('\n')}
              onChange={(e) =>
                onPatch({
                  ...data,
                  eligibility: {
                    ...eligibility,
                    inclusion_criteria: e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
            <Label>Exclusion (one per line)</Label>
            <Txa
              value={asStrArr(eligibility.exclusion_criteria).join('\n')}
              onChange={(e) =>
                onPatch({
                  ...data,
                  eligibility: {
                    ...eligibility,
                    exclusion_criteria: e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
          </>
        ) : (
          <Grid2>
            {asStrArr(eligibility.inclusion_criteria).length > 0 && (
              <Card>
                <CardLabel>Inclusion</CardLabel>
                <Ul style={{ marginTop: 8 }}>
                  {asStrArr(eligibility.inclusion_criteria).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </Ul>
              </Card>
            )}
            {asStrArr(eligibility.exclusion_criteria).length > 0 && (
              <Card>
                <CardLabel>Exclusion</CardLabel>
                <Ul style={{ marginTop: 8 }}>
                  {asStrArr(eligibility.exclusion_criteria).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </Ul>
              </Card>
            )}
          </Grid2>
        )}
      </Section>
      )}

      <Section>
        <SectionKicker>Intervention</SectionKicker>
        {isPageEdit ? (
          <>
            <Label>Device type</Label>
            <Inp
              value={asStr(intervention.device_type)}
              onChange={(e) => onPatch({ ...data, intervention: { ...intervention, device_type: e.target.value } })}
            />
            <Label>Acute session summary (prose, optional)</Label>
            <Txa
              value={asStr(intervention.acute_session_summary)}
              onChange={(e) =>
                onPatch({ ...data, intervention: { ...intervention, acute_session_summary: e.target.value } })
              }
              style={{ minHeight: 72 }}
            />
            <Label>Sham protocol (only used in view/PDF if a sham arm has planned N &gt; 0)</Label>
            <Txa
              value={asStr(intervention.sham_protocol)}
              onChange={(e) => onPatch({ ...data, intervention: { ...intervention, sham_protocol: e.target.value } })}
              style={{ minHeight: 64 }}
            />
            <Label>Active protocol (JSON object, array of strings, or a single string)</Label>
            <Txa
              value={
                typeof intervention.active_protocol === 'string'
                  ? intervention.active_protocol
                  : JSON.stringify(
                      intervention.active_protocol && typeof intervention.active_protocol === 'object'
                        ? intervention.active_protocol
                        : Array.isArray(intervention.active_protocol)
                          ? intervention.active_protocol
                          : {},
                      null,
                      2
                    )
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (raw.trim().startsWith('{') || raw.trim().startsWith('[')) {
                  try {
                    const parsed = JSON.parse(raw);
                    onPatch({ ...data, intervention: { ...intervention, active_protocol: parsed } });
                  } catch {
                    /* keep typing */
                  }
                } else {
                  onPatch({ ...data, intervention: { ...intervention, active_protocol: raw } });
                }
              }}
              style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 12 }}
            />
          </>
        ) : (
          <Grid2>
            {asStr(intervention.device_type) && (
              <Card>
                <CardLabel>Device</CardLabel>
                <CardText>{asStr(intervention.device_type)}</CardText>
              </Card>
            )}
            {asStr(intervention.acute_session_summary) && (
              <Card style={{ gridColumn: '1 / -1' }}>
                <CardLabel>Acute stimulation session</CardLabel>
                <CardText>{asStr(intervention.acute_session_summary)}</CardText>
              </Card>
            )}
            {activeProtocolIsNonempty(intervention.active_protocol) && (
              <Card style={{ gridColumn: '1 / -1' }}>
                <CardLabel>Active protocol</CardLabel>
                <ActiveProtocolView value={intervention.active_protocol} />
              </Card>
            )}
            {showShamProtocolReadView(intervention, cohort) && (
              <Card style={{ gridColumn: '1 / -1' }}>
                <CardLabel>Sham protocol</CardLabel>
                <CardText>{asStr(intervention.sham_protocol)}</CardText>
              </Card>
            )}
          </Grid2>
        )}
      </Section>

      {(isPageEdit || visits.length > 0) && (
      <Section>
        <SectionKicker>Study visits</SectionKicker>
        {isPageEdit ? (
          <Txa
            value={JSON.stringify(visits, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (Array.isArray(parsed)) {
                  onPatch({ ...data, study_visits: parsed });
                }
              } catch {
                /* ignore */
              }
            }}
            style={{ minHeight: 220, fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : visitsForView.length === 0 ? (
          <Body style={{ color: '#64748b' }}>
            No visits to display after applying synopsis rules (e.g. empty randomization row omitted for mechanistic
            profiles).
          </Body>
        ) : (
          <Grid2>
            {visitsForView.map((v, i) => {
              const visit = v as Record<string, unknown>;
              const procs = Array.isArray(visit.procedures) ? visit.procedures : [];
              const title = visitRecordLabel(visit) || `Visit ${i + 1}`;
              return (
                <Card key={i}>
                  <CardLabel>{title}</CardLabel>
                  {asStr(visit.window) && (
                    <CardText style={{ marginBottom: 8 }}>
                      <strong>Timing / window:</strong> {asStr(visit.window)}
                    </CardText>
                  )}
                  {asStr(visit.notes) && <CardText style={{ marginBottom: 8 }}>{asStr(visit.notes)}</CardText>}
                  {procs.length > 0 ? (
                    <Ul style={{ marginTop: 8 }}>
                      {procs.map((p, j) => (
                        <li key={j}>{String(p)}</li>
                      ))}
                    </Ul>
                  ) : (
                    !asStr(visit.notes) &&
                    !asStr(visit.window) && <CardText style={{ color: '#94a3b8' }}>—</CardText>
                  )}
                </Card>
              );
            })}
          </Grid2>
        )}
      </Section>
      )}

      {(isPageEdit ||
        outcomeEndpointList(outcomes, 'primary').length > 0 ||
        outcomeEndpointList(outcomes, 'secondary').length > 0 ||
        outcomeEndpointList(outcomes, 'exploratory').length > 0 ||
        asStrArr(outcomes.autonomic_endpoints).length > 0) && (
      <Section>
        <SectionKicker>Outcomes</SectionKicker>
        {isPageEdit ? (
          <Txa
            value={JSON.stringify(outcomes, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  onPatch({ ...data, outcomes: parsed });
                }
              } catch {
                /* ignore */
              }
            }}
            style={{ minHeight: 180, fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : (
          <Grid2>
            {(['primary', 'secondary', 'exploratory'] as const).map((k) => {
              const arr = outcomeEndpointList(outcomes, k);
              if (arr.length === 0) return null;
              const label =
                k === 'primary' ? 'Primary endpoints' : k === 'secondary' ? 'Secondary endpoints' : 'Exploratory endpoints';
              return (
                <Card key={k}>
                  <CardLabel>{label}</CardLabel>
                  <Ul style={{ marginTop: 8 }}>
                    {arr.map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </Ul>
                </Card>
              );
            })}
            {asStrArr(outcomes.autonomic_endpoints).length > 0 && (
              <Card>
                <CardLabel>Autonomic endpoints</CardLabel>
                <Ul style={{ marginTop: 8 }}>
                  {asStrArr(outcomes.autonomic_endpoints).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </Ul>
              </Card>
            )}
          </Grid2>
        )}
      </Section>
      )}

      <Section>
        <SectionKicker>Data collection</SectionKicker>
        {isPageEdit ? (
          <Txa
            value={JSON.stringify(dataCollection, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  onPatch({ ...data, data_collection: parsed });
                }
              } catch {
                /* ignore */
              }
            }}
            style={{ minHeight: 160, fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : (
          <Grid2>
            {asStrArr(dataCollection.modalities).length > 0 && (
              <Card>
                <CardLabel>Modalities</CardLabel>
                <Ul style={{ marginTop: 8 }}>
                  {asStrArr(dataCollection.modalities).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </Ul>
              </Card>
            )}
            {asStr(dataCollection.data_storage) && (
              <Card>
                <CardLabel>Storage</CardLabel>
                <CardText>{asStr(dataCollection.data_storage)}</CardText>
              </Card>
            )}
          </Grid2>
        )}
      </Section>

      {(isPageEdit || Object.keys(stats).length > 0 || mechanistic) && (
      <Section>
        <SectionKicker>Statistical analysis</SectionKicker>
        {isPageEdit ? (
          <Txa
            value={JSON.stringify(stats, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  onPatch({ ...data, statistical_analysis: parsed });
                }
              } catch {
                /* ignore */
              }
            }}
            style={{ minHeight: 120, fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : (
          <>
            {(asStr(stats.summary_narrative) || asStr(stats.narrative) || asStr(stats.synopsis)) && (
              <Body style={{ marginBottom: 16, lineHeight: 1.65 }}>
                {asStr(stats.summary_narrative) || asStr(stats.narrative) || asStr(stats.synopsis)}
              </Body>
            )}
            {mechanistic &&
              !asStr(stats.summary_narrative) &&
              !asStr(stats.narrative) &&
              !asStr(stats.synopsis) && (
                <Body style={{ marginBottom: 16, lineHeight: 1.65, color: '#475569' }}>
                  Planned analyses emphasize within-subject stimulus–response relationships, descriptive summaries, and
                  exploratory correlations suited to a mechanistic pilot; this document is not framed as a definitive
                  efficacy or sham-controlled randomized trial unless you add a statistical synopsis below.
                </Body>
              )}
            <Grid2>
              {Object.entries(stats)
                .filter(([k]) => !['summary_narrative', 'narrative', 'synopsis'].includes(k))
                .filter(([, v]) => {
                  if (v == null || v === '') return false;
                  if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0)
                    return false;
                  return true;
                })
                .map(([k, v]) => (
                  <Card key={k}>
                    <CardLabel>{humanizeFieldKey(k)}</CardLabel>
                    <CardText>
                      {typeof v === 'object' && v !== null ? JSON.stringify(v, null, 2) : String(v)}
                    </CardText>
                  </Card>
                ))}
            </Grid2>
          </>
        )}
      </Section>
      )}

      <Section>
        <SectionKicker>Budget</SectionKicker>
        {isPageEdit ? (
          <Txa
            value={JSON.stringify(budget, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  onPatch({ ...data, budget: parsed });
                }
              } catch {
                /* ignore */
              }
            }}
            style={{ minHeight: 280, fontFamily: 'monospace', fontSize: 12 }}
          />
        ) : (
          <>
            {(() => {
              const currency = asStr(budget.currency) || 'USD';
              const { cohortN, rows, categorySubtotal } = computeBudgetCategoryRows(budget, cohort);
              const optionalSubtotal = computeOptionalSubtotal(budget);
              const grandTotal = Math.round(categorySubtotal + optionalSubtotal);
              const storedTotal = numField(budget.estimated_total);
              const totalDrift =
                storedTotal != null && grandTotal > 0 && Math.round(storedTotal) !== grandTotal;

              return (
                <>
                  <Body style={{ marginBottom: 8 }}>
                    <strong>{currency}</strong>
                    {cohortN != null && (
                      <>
                        {' '}
                        · Cohort <strong>N = {cohortN}</strong>
                        {cohort.target_sample_size != null
                          ? ' (from target sample size)'
                          : ' (sum of arm sizes)'}
                        . Per-patient lines use this N for units and line totals.
                      </>
                    )}
                    {cohortN == null && (
                      <> · Set <code>cohort.target_sample_size</code> or arm sizes to drive per-patient budget math.</>
                    )}
                  </Body>
                  {grandTotal > 0 && (
                    <Body style={{ marginBottom: 8, fontSize: 15 }}>
                      <strong>Estimated total (auto):</strong> {currency}{' '}
                      <strong>{grandTotal.toLocaleString()}</strong>
                      <span style={{ color: '#64748b', fontWeight: 500 }}>
                        {' '}
                        = categories {categorySubtotal.toLocaleString()} + optional{' '}
                        {optionalSubtotal.toLocaleString()}
                      </span>
                      {totalDrift && (
                        <span style={{ color: '#b45309', display: 'block', marginTop: 6, fontSize: 13 }}>
                          JSON <code>estimated_total</code> is {Math.round(storedTotal!).toLocaleString()}; it will be
                          replaced with the calculated value when you save.
                        </span>
                      )}
                    </Body>
                  )}
                  {rows.length > 0 && (
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Type</th>
                            <th>Unit $</th>
                            <th>Units (N)</th>
                            <th>Total</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.key}>
                              <td>{r.name}</td>
                              <td>{r.costType}</td>
                              <td>{r.unitCostDisp}</td>
                              <td>{r.unitsDisp}</td>
                              <td>{r.totalDisp}</td>
                              <td>{r.notes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </>
              );
            })()}
            {optionalBudgetList(budget).length > 0 && (
              <>
                <H2 style={{ marginTop: 20 }}>Optional costs</H2>
                <Body style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                  Optional amounts below are included in <strong>Estimated total (auto)</strong> when you save (and in the
                  stored <code>budget.estimated_total</code>).
                </Body>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Est. total</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optionalBudgetList(budget).map((row, i) => {
                        const r = row as Record<string, unknown>;
                        const total = r.estimated_total ?? r.suggested_total;
                        return (
                          <tr key={i}>
                            <td>{asStr(r.name)}</td>
                            <td>{total != null ? String(total) : '—'}</td>
                            <td>{asStr(r.notes)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableWrap>
              </>
            )}
          </>
        )}
      </Section>

      <Section>
        <SectionKicker>Scientific references</SectionKicker>
        {isPageEdit ? (
          <>
            <Body style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
              Paste one or more BibTeX blocks (e.g. <code>@article&#123;key, …&#125;</code>). You may also store{' '}
              <code>scientific_references</code> in JSON as a string or an array of objects with{' '}
              <code>cite_key</code>, <code>title</code>, <code>author</code>, <code>journal</code>, <code>year</code>.
            </Body>
            <Txa
              value={referencesEditString}
              onChange={(e) => patchRoot('scientific_references', e.target.value)}
              style={{ minHeight: 280, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
            />
          </>
        ) : parsedRefs.length > 0 ? (
          <RefOrderedList>
            {parsedRefs.map((r, i) => (
              <RefLi key={`${r.citeKey}-${i}`}>
                <ReferenceFormatted r={r} />
              </RefLi>
            ))}
          </RefOrderedList>
        ) : (
          <Body style={{ fontSize: 14, color: '#94a3b8' }}>None listed.</Body>
        )}
      </Section>

      {extraEntries.length > 0 && (
        <Section>
          <SectionKicker>Additional fields</SectionKicker>
          {extraEntries.map(([k, v]) => (
            <Card key={k} style={{ marginBottom: 12 }}>
              <CardLabel>{k}</CardLabel>
              {isPageEdit ? (
                <Txa
                  value={typeof v === 'string' ? v : JSON.stringify(v, null, 2)}
                  onChange={(e) => {
                    let next: unknown = e.target.value;
                    try {
                      next = JSON.parse(e.target.value);
                    } catch {
                      next = e.target.value;
                    }
                    onPatch({ ...data, [k]: next });
                  }}
                  style={{ minHeight: 80, fontFamily: 'monospace', fontSize: 12, marginBottom: 0 }}
                />
              ) : (
                <PreBlock style={{ margin: 0 }}>{typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}</PreBlock>
              )}
            </Card>
          ))}
        </Section>
      )}
    </>
  );
}

const ResearchProjectEditorPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('view');
  const [title, setTitle] = useState('');
  const [config, setConfig] = useState<Config>({});
  const [pageDraft, setPageDraft] = useState<Config | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [wordBusy, setWordBusy] = useState(false);

  const pdfBodyRef = useRef<HTMLDivElement>(null);
  const validId = projectId && UUID_RE.test(projectId);

  const load = useCallback(async () => {
    if (!validId || !projectId) return;
    setError(null);
    setSavedMsg(null);
    setLoading(true);
    try {
      const res = await api.getResearchProject(projectId);
      const p = res.data?.project;
      if (!p) throw new Error('No project data');
      const nextConfig = (p.config && typeof p.config === 'object' && !Array.isArray(p.config) ? p.config : {}) as Config;
      setTitle(p.title || '');
      setConfig(nextConfig);
      setJsonText(JSON.stringify(nextConfig, null, 2));
      setPageDraft(null);
      setUpdatedAt(p.updated_at);
      setMode('view');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, validId]);

  useEffect(() => {
    void load();
  }, [load]);

  const displayForDoc = useMemo(() => {
    if (mode === 'edit-page' && pageDraft) return pageDraft;
    return config;
  }, [mode, pageDraft, config]);

  const handleEnterEditPage = () => {
    setPageDraft(deepClone(config));
    setMode('edit-page');
    setSavedMsg(null);
    setError(null);
  };

  const handleEnterEditJson = () => {
    const src = mode === 'edit-page' && pageDraft ? pageDraft : config;
    setJsonText(JSON.stringify(src, null, 2));
    setMode('edit-json');
    setSavedMsg(null);
    setError(null);
  };

  const handleView = () => {
    setPageDraft(null);
    setMode('view');
    setSavedMsg(null);
    setError(null);
  };

  const savePageDraft = async () => {
    if (!validId || !projectId || !pageDraft) return;
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const payload = withAutoBudgetEstimatedTotal(pageDraft);
      await api.patchResearchProject(projectId, { title: title.trim(), config: payload });
      setConfig(deepClone(payload));
      setPageDraft(deepClone(payload));
      setJsonText(JSON.stringify(payload, null, 2));
      setSavedMsg('Saved. budget.estimated_total updated from line items.');
      const res = await api.getResearchProject(projectId);
      setUpdatedAt(res.data?.project?.updated_at ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveJson = async () => {
    if (!validId || !projectId) return;
    let next: Config;
    const trimmed = jsonText.trim();
    const extracted = extractFirstJsonObject(jsonText);
    const candidate = extracted ?? trimmed;
    try {
      const parsed = JSON.parse(candidate);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('Config must be a JSON object (not an array or primitive).');
        return;
      }
      next = parsed as Config;
    } catch (err: unknown) {
      const hint =
        err instanceof SyntaxError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unknown parse error';
      if (extracted === null && !trimmed.startsWith('{')) {
        setError(
          'No JSON object found. The textarea must contain a single {...} config. If you pasted a whole chat message (e.g. text starting with "Use this prompt"), delete everything before the first { and after the matching }, or copy from docs/research_portal/examples/auricular_pneumatic_mechanistic_migraine.json only.'
        );
        return;
      }
      setError(
        `Invalid JSON (${hint}). If the error mentions an unexpected word at the start, remove any instructions before the opening {. For BibTeX inside "scientific_references", use \\n between lines inside the quoted string—no raw line breaks inside the quotes.`
      );
      return;
    }
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const payload = withAutoBudgetEstimatedTotal(next);
      await api.patchResearchProject(projectId, { title: title.trim(), config: payload });
      setConfig(deepClone(payload));
      setPageDraft(null);
      setJsonText(JSON.stringify(payload, null, 2));
      setSavedMsg('Saved. budget.estimated_total updated from line items.');
      const res = await api.getResearchProject(projectId);
      setUpdatedAt(res.data?.project?.updated_at ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = useCallback(async () => {
    const el = pdfBodyRef.current;
    if (!el) return;
    setPdfBusy(true);
    const docTitle = asStr(config.project_title) || asStr(config.title) || title.trim() || 'research-project';
    const filename = pdfFilenameFromProject(docTitle, title);
    try {
      await html2pdf()
        .set({
          margin: [12, 12, 16, 12],
          filename,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            letterRendering: true,
            ignoreElements: (node: Element) =>
              (node as HTMLElement).classList?.contains('research-no-pdf') ?? false,
          },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: {
            mode: ['css', 'legacy'],
            avoid: ['tr', 'thead', 'header'],
          },
        })
        .from(el)
        .save();
    } catch (err) {
      console.error(err);
      window.alert('PDF export failed. Try Download PDF again.');
    } finally {
      setPdfBusy(false);
    }
  }, [config.project_title, config.title, title]);

  const handleDownloadWord = useCallback(async () => {
    const docTitle = asStr(config.project_title) || asStr(config.title) || title.trim() || 'research-project';
    setWordBusy(true);
    setError(null);
    try {
      const blob = await buildResearchProjectDocxBlob(config);
      triggerDocxDownload(blob, docxFilenameFromProject(docTitle, title));
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Word export failed.');
    } finally {
      setWordBusy(false);
    }
  }, [config, title]);

  const handleDownloadPdfRef = useRef(handleDownloadPdf);
  handleDownloadPdfRef.current = handleDownloadPdf;

  useEffect(() => {
    if (mode !== 'view') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== 'p') return;
      e.preventDefault();
      e.stopPropagation();
      void handleDownloadPdfRef.current();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [mode]);

  const handleDelete = async () => {
    if (!validId || !projectId) return;
    if (!window.confirm('Delete this research project permanently?')) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteResearchProject(projectId);
      navigate('/research/portal');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  if (!validId) {
    return (
      <Root>
        <Back to="/research/portal">← Research Portal</Back>
        <Err>Invalid project id.</Err>
      </Root>
    );
  }

  const showDoc = mode === 'view' || mode === 'edit-page';
  const dbTitleRow = (
    <>
      <Label htmlFor="rp-db-title">List title (portal card)</Label>
      <Inp
        id="rp-db-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Short label for the project list"
        style={{ maxWidth: '32rem' }}
      />
    </>
  );

  return (
    <Root>
      <StickyBar>
        <BarLeft>
          <Back to="/research/portal">← Research Portal</Back>
          <BarTitle>{title.trim() || asStr(displayForDoc.project_title) || 'Research project'}</BarTitle>
        </BarLeft>
        <TopActions>
          {mode === 'view' && (
            <>
              <Btn type="button" $variant="outline" onClick={handleEnterEditPage}>
                Edit page
              </Btn>
              <Btn type="button" $variant="outline" onClick={handleEnterEditJson}>
                Edit JSON config
              </Btn>
            </>
          )}
          {mode === 'edit-page' && (
            <>
              <Btn type="button" $variant="ghost" onClick={handleView}>
                View
              </Btn>
              <Btn type="button" $variant="outline" onClick={handleEnterEditJson}>
                Edit JSON config
              </Btn>
            </>
          )}
          {mode === 'edit-json' && (
            <>
              <Btn type="button" $variant="ghost" onClick={handleView}>
                View
              </Btn>
              <Btn
                type="button"
                $variant="outline"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(jsonText) as unknown;
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      setPageDraft(parsed as Config);
                    } else {
                      setPageDraft(deepClone(config));
                    }
                  } catch {
                    setPageDraft(deepClone(config));
                  }
                  setMode('edit-page');
                  setError(null);
                }}
              >
                Edit page
              </Btn>
            </>
          )}
        </TopActions>
      </StickyBar>

      {error && <Err>{error}</Err>}
      {savedMsg && <Ok>{savedMsg}</Ok>}

      {loading ? (
        <p style={{ color: '#64748b' }}>Loading…</p>
      ) : mode === 'edit-json' ? (
        <>
          {dbTitleRow}
          <Label htmlFor="rp-json">Full config (JSON)</Label>
          <Body style={{ fontSize: 13, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>
            Paste <strong>only</strong> the JSON object (<code>{'{'}</code> … <code>{'}'}</code>), not chat instructions.
            Markdown fences (<code>```json</code>) are stripped automatically. Example file:{' '}
            <code>docs/research_portal/examples/auricular_pneumatic_mechanistic_migraine.json</code>.
          </Body>
          <JsonArea
            id="rp-json"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            spellCheck={false}
            autoComplete="off"
          />
          <ActionRow>
            <Btn type="button" $variant="primary" onClick={() => void saveJson()} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Btn>
            <Btn type="button" $variant="ghost" onClick={() => void load()} disabled={saving}>
              Reload from server
            </Btn>
            <Btn type="button" $variant="danger" onClick={() => void handleDelete()} disabled={saving}>
              Delete project
            </Btn>
          </ActionRow>
        </>
      ) : (
        <>
          {(mode === 'edit-page' || mode === 'view') && mode === 'edit-page' && dbTitleRow}
          {showDoc && (
            <>
              <ResearchPdfPrintStyles />
              <PdfCaptureWrap ref={pdfBodyRef} className="research-pdf-body">
                <DocumentView
                  data={displayForDoc}
                  isPageEdit={mode === 'edit-page'}
                  onPatch={(patch) => {
                    if (mode === 'edit-page') {
                      setPageDraft(patch);
                    }
                  }}
                />
              </PdfCaptureWrap>
            </>
          )}
          {mode === 'edit-page' && (
            <ActionRow>
              <Btn type="button" $variant="primary" onClick={() => void savePageDraft()} disabled={saving || !pageDraft}>
                {saving ? 'Saving…' : 'Save changes'}
              </Btn>
              <Btn type="button" $variant="ghost" onClick={() => void load()} disabled={saving}>
                Reload from server
              </Btn>
              <Btn type="button" $variant="danger" onClick={() => void handleDelete()} disabled={saving}>
                Delete project
              </Btn>
            </ActionRow>
          )}
          {mode === 'view' && (
            <ActionRow>
              <Btn type="button" $variant="ghost" onClick={() => void load()} disabled={saving}>
                Reload from server
              </Btn>
              <Btn type="button" $variant="danger" onClick={() => void handleDelete()} disabled={saving}>
                Delete project
              </Btn>
            </ActionRow>
          )}
          {mode === 'view' && showDoc && (
            <PostResearchExport className="research-no-pdf">
              <ExportPdfBtn type="button" disabled={pdfBusy || wordBusy || loading} onClick={() => void handleDownloadPdf()}>
                {pdfBusy ? 'Preparing PDF…' : 'Download PDF'}
              </ExportPdfBtn>
              <ExportWordBtn type="button" disabled={wordBusy || pdfBusy || loading} onClick={() => void handleDownloadWord()}>
                {wordBusy ? 'Preparing Word…' : 'Download Word'}
              </ExportWordBtn>
            </PostResearchExport>
          )}
        </>
      )}

      {updatedAt && (
        <Meta>
          Last updated (server): {new Date(updatedAt).toLocaleString()} · ID: {projectId}
        </Meta>
      )}
    </Root>
  );
};

export default ResearchProjectEditorPage;
